import React, { useEffect, useState } from 'react';
import { X, MapPin, Package2, Clock, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ItemLocationInfo } from '../../../../../services/inventoryCorrectionService';
import { ScanTextInput } from '../../../../common/inputs';

interface LocationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (location: ItemLocationInfo) => void;
  /** Skann en lokasjon for å velge korreksjonsmål. Returnerer oppløst lokasjon, eller null hvis ugyldig. */
  onScanLocation?: (code: string) => Promise<ItemLocationInfo | null>;
  itemNo: string;
  itemDescription: string;
  /** Item.ItemUnitTxt — vises under artikkelnr/beskrivelse. */
  itemUnitTxt?: string;
  locations: ItemLocationInfo[];
  loading: boolean;
}

export const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onScanLocation,
  itemNo,
  itemDescription,
  itemUnitTxt,
  locations,
  loading
}) => {
  const { t } = useTranslation();
  const [showEmpty, setShowEmpty] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  // Nullstill skann/visning hver gang modalen åpnes (komponenten forblir montert).
  useEffect(() => {
    if (isOpen) {
      setShowEmpty(false);
      setScanValue('');
      setScanError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Skjul lokasjoner med saldo = 0 som default (behold negative — korreksjon kan rette dem).
  const emptyCount = locations.filter(l => l.totalQuantity === 0).length;
  const visibleLocations = showEmpty ? locations : locations.filter(l => l.totalQuantity !== 0);

  const handleSelect = (location: ItemLocationInfo) => {
    onSelect(location);
    onClose();
  };

  const handleScan = async () => {
    const code = scanValue.trim();
    if (!code) return;
    setScanError(null);

    // 1) Match mot listen varen ligger på (også tomme — kan korrigere inn på eksisterende tom lokasjon)
    const match = locations.find(l => l.locationName.toLowerCase() === code.toLowerCase());
    if (match) { handleSelect(match); return; }

    // 2) Valider som ny lokasjon via forelder
    if (onScanLocation) {
      setScanning(true);
      try {
        const resolved = await onScanLocation(code);
        if (resolved) { handleSelect(resolved); return; }
      } finally {
        setScanning(false);
      }
    }
    setScanError(t('warehouse.modules.inventoryCorrection.messages.scanLocationNotFound', { '0': code }, `Lokasjon ${code} ble ikke funnet`));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
        <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl sm:max-w-2xl sm:max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <Package2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold truncate">{t('warehouse.modules.inventoryCorrection.modals.selectLocationForCorrection')}</h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {itemNo} - {itemDescription}
                </p>
                {itemUnitTxt && (
                  <p className="text-xs text-gray-500 truncate">{itemUnitTxt}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin h-8 w-8 text-red-500" />
                <span className="ml-2 text-sm sm:text-base">{t('warehouse.modules.inventoryCorrection.messages.loadingLocations')}</span>
              </div>
            ) : (
              <>
                {/* Skann lokasjon for å bestemme korreksjonsmål */}
                <div className="mb-4">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t('warehouse.modules.inventoryCorrection.modals.scanLocationLabel', {}, 'Skann lokasjon for å korrigere herfra')}
                  </label>
                  <div className="flex gap-2">
                    <ScanTextInput
                      value={scanValue}
                      onChange={(e) => { setScanValue(e.target.value); if (scanError) setScanError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(); } }}
                      placeholder={t('warehouse.modules.inventoryCorrection.modals.scanLocationPlaceholder', {}, 'Skann eller skriv lokasjon...')}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[44px]"
                    />
                    <button
                      onClick={handleScan}
                      disabled={!scanValue.trim() || scanning}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 min-h-[44px]"
                    >
                      {scanning
                        ? <Loader className="h-4 w-4 animate-spin" />
                        : t('warehouse.modules.inventoryCorrection.modals.useLocation', {}, 'Bruk')}
                    </button>
                  </div>
                  {scanError && (
                    <p className="text-xs sm:text-sm text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {scanError}
                    </p>
                  )}
                </div>

                {locations.length === 0 ? (
                  <div className="text-center py-8">
                    <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-sm sm:text-base text-gray-500">{t('warehouse.modules.inventoryCorrection.messages.noLocationsFound')}</p>
                    <p className="text-xs sm:text-sm text-gray-400 mt-1">
                      {t('warehouse.modules.inventoryCorrection.messages.itemNotFoundOnAnyLocations')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Antall + toggle for tomme lokasjoner */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-xs sm:text-sm text-gray-600">
                        {t('warehouse.modules.inventoryCorrection.messages.itemFoundOnLocations', { '0': visibleLocations.length, '1': visibleLocations.length !== 1 ? 'er' : '' })}
                      </div>
                      {emptyCount > 0 && (
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 cursor-pointer select-none min-h-[44px] sm:min-h-0">
                          <input
                            type="checkbox"
                            checked={showEmpty}
                            onChange={(e) => setShowEmpty(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          {t('warehouse.modules.inventoryCorrection.modals.showEmptyLocations', { '0': emptyCount }, `Vis tomme lokasjoner (${emptyCount})`)}
                        </label>
                      )}
                    </div>

                    {visibleLocations.length === 0 ? (
                      <div className="text-center py-6 text-sm text-gray-500">
                        {t('warehouse.modules.inventoryCorrection.messages.noLocationsWithBalance', {}, 'Ingen lokasjoner med saldo. Huk av «Vis tomme lokasjoner» for å se alle.')}
                      </div>
                    ) : (
                      visibleLocations.map((location, index) => (
                        <div
                          key={`${location.locationName}-${index}`}
                          onClick={() => handleSelect(location)}
                          className="border border-gray-200 rounded-lg p-4 hover:border-red-300 hover:bg-red-50 cursor-pointer transition-all active:bg-red-100 min-h-[44px]"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
                              <MapPin className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5 sm:mt-0" />
                              <div className="min-w-0">
                                <div className="font-semibold font-mono text-sm break-all">
                                  {location.locationName}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                  {t('warehouse.general.warehouse')}: {location.warehouseID}
                                </div>
                              </div>
                            </div>

                            <div className="text-left sm:text-right flex-shrink-0">
                              <div className={`font-semibold text-sm ${
                                location.availableQuantity > 0
                                  ? 'text-green-600'
                                  : location.availableQuantity === 0
                                    ? 'text-gray-500'
                                    : 'text-red-600'
                              }`}>
                                {location.availableQuantity} {t('warehouse.general.available')}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500">
                                {location.totalQuantity} {t('warehouse.modules.inventoryCorrection.labels.total')}
                                {location.reservedQuantity > 0 && (
                                  <span className="ml-1 text-orange-600">
                                    ({location.reservedQuantity} {t('warehouse.general.reserved')})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Additional info */}
                          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500">
                            {location.binCodePrimary && (
                              <div className="flex items-center whitespace-nowrap">
                                <CheckCircle className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                                <span>{t('warehouse.modules.inventoryCorrection.labels.primaryLocation')}</span>
                              </div>
                            )}

                            {location.binCodeFixed && (
                              <div className="flex items-center whitespace-nowrap">
                                <span className="w-3 h-3 bg-blue-500 rounded-full mr-1 flex-shrink-0"></span>
                                <span>{t('warehouse.modules.inventoryCorrection.labels.fixedLocation')}</span>
                              </div>
                            )}

                            {location.lastMovementDate && (
                              <div className="flex items-center">
                                <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="break-words">
                                  {t('warehouse.modules.inventoryCorrection.labels.lastMoved')}: {location.lastMovementDate.toLocaleDateString('no-NO')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 sm:p-6 border-t space-x-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2.5 sm:py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm min-h-[44px] sm:min-h-0"
            >
              {t('common.buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
